import React, { useState } from "react";
import { Send, MessageCircleMore, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import Container from "../components/Container";
import Breadcrumb from "../components/Breadcrumb";
import { cls } from "../utils/helpers";
import {
  WHATSAPP_NUM,
  COMPANY_NAME,
} from "../utils/constants";
import { apiClient } from "../utils/apiClient";

function openWhatsApp(preset) {
  const num = WHATSAPP_NUM.replace(/\D/g, "");
  const text = encodeURIComponent(
    preset ||
      "Bonjour, je souhaite échanger au sujet de vos prestations / software."
  );
  window.open(`https://wa.me/${num}?text=${text}`, "_blank");
}

function HtmlText({ html, className }) {
  return <p className={className} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function RecruitmentPage({ p, t }) {
  const [type, setType] = useState("spont");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [ref, setRef] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [portfolio, setPortfolio] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  async function submitApplication(e) {
    e.preventDefault();

    if (!name || !email || !phone || !linkedin || !portfolio) {
      setError(t("form.error.required") || "Please fill in all required fields.");
      return;
    }

    if (!consent) {
      setError(t("form.error.consent") || "Please accept the privacy policy.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    const result = await apiClient.sendCareerForm({
      application_type: type,
      full_name: name,
      email,
      phone,
      target_role: role,
      job_reference: ref,
      linkedin_profile: linkedin,
      cv_link: portfolio,
    });

    setLoading(false);

    if (result.success) {
      setSuccess(true);
      setName("");
      setEmail("");
      setPhone("");
      setRole("");
      setRef("");
      setLinkedin("");
      setPortfolio("");
      setConsent(false);
    } else {
      if (result.status === 429) {
        setError(t("form.error.rate_limit") || "Too many requests. Please try again in an hour.");
      } else {
        setError(result.error || t("form.error.generic") || "An error occurred. Please try again.");
      }
    }
  }

  return (
    <>
      <Container>
        <Breadcrumb
          items={[{ label: t("crumb.home"), href: "/" }, { label: t("nav.recruitment") }]}
          p={p}
        />
      </Container>
      <section className={p.hero}>
        <Container>
          <div className="py-12">
            <h1 className={cls("text-3xl font-extrabold", p.cardTitle)}>
              {t("jobs.title").replace("{company}", COMPANY_NAME)}
            </h1>
            <HtmlText
              html={t("jobs.subtitle")}
              className={cls("mt-2 max-w-3xl", p.paragraph)}
            />
            <div className="mt-6 flex flex-wrap gap-2">
              <a href="#jobs-spont" className={p.chip}>
                {t("jobs.anchor.spont")}
              </a>
              <a href="#jobs-offer" className={p.chip}>
                {t("jobs.anchor.offer")}
              </a>
            </div>
          </div>
        </Container>
      </section>

      <section id="jobs-form" className="py-10">
        <Container>
          <form
            onSubmit={submitApplication}
            className={cls("grid gap-4 md:grid-cols-2 backdrop-blur rounded-2xl p-6", p.formContainer)}
          >
            <div className="md:col-span-2">
              <label className="text-sm font-semibold">{t("jobs.type")}</label>
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  onClick={() => setType("spont")}
                  disabled={loading}
                  className={cls(
                    "rounded-xl px-3 py-1.5 text-sm",
                    type === "spont" ? p.formButtonActive : p.formButtonIdle,
                    loading && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {t("jobs.type.spont")}
                </button>
                <button
                  type="button"
                  onClick={() => setType("offer")}
                  disabled={loading}
                  className={cls(
                    "rounded-xl px-3 py-1.5 text-sm",
                    type === "offer" ? p.formButtonActive : p.formButtonIdle,
                    loading && "opacity-60 cursor-not-allowed"
                  )}
                >
                  {t("jobs.type.offer")}
                </button>
              </div>
            </div>

            <div id="jobs-spont" className="md:col-span-2" />

            <div>
              <label className="text-sm font-semibold">{t("form.name")} *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("form.placeholder.name")}
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("form.email")} *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("form.placeholder.email")}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">{t("form.phone")} *</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("form.placeholder.phone")}
                required
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("jobs.role")}</label>
              <input
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("jobs.placeholder.role")}
                disabled={loading}
              />
            </div>

            <div id="jobs-offer" className="md:col-span-2" />

            <div>
              <label className="text-sm font-semibold">{t("jobs.ref")}</label>
              <input
                value={ref}
                onChange={(e) => setRef(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("jobs.placeholder.ref")}
                disabled={loading}
              />
            </div>
            <div>
              <label className="text-sm font-semibold">{t("jobs.linkedin")} *</label>
              <input
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("jobs.placeholder.linkedin")}
                required
                disabled={loading}
              />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold">{t("jobs.portfolio")} *</label>
              <input
                value={portfolio}
                onChange={(e) => setPortfolio(e.target.value)}
                className={cls("mt-1 w-full rounded-xl p-3", p.formInput)}
                placeholder={t("jobs.placeholder.portfolio")}
                required
                disabled={loading}
              />
            </div>

            <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={loading}
              />
              {t("jobs.consent")}
            </label>

            {success && (
              <div className={cls("md:col-span-2 flex items-center gap-2 rounded-xl p-4 border", p.successMessage)}>
                <CheckCircle size={20} className="flex-shrink-0" />
                <p className="text-sm font-medium">
                  {t("form.success") || "Application sent successfully! We'll get back to you soon."}
                </p>
              </div>
            )}

            {error && (
              <div className={cls("md:col-span-2 flex items-center gap-2 rounded-xl p-4 border", p.errorMessage)}>
                <AlertCircle size={20} className="flex-shrink-0" />
                <p className="text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="md:col-span-2 flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={loading}
                className={cls(
                  "inline-flex items-center gap-2",
                  p.ctaPrimary,
                  loading && "opacity-60 cursor-not-allowed"
                )}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t("form.sending") || "Sending..."}
                  </>
                ) : (
                  <>
                    <Send size={16} /> {t("jobs.send")}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => openWhatsApp("Bonjour, je souhaite candidater.")}
                className={cls("inline-flex items-center gap-2", p.ctaSecondary)}
                disabled={loading}
              >
                <MessageCircleMore size={16} /> WhatsApp
              </button>
            </div>
            <p className={cls("md:col-span-2 text-xs", p.formText)}>
              {t("jobs.data")}
            </p>
          </form>
        </Container>
      </section>
    </>
  );
}
